import React, { useState, useEffect } from 'react';
import { X, Wallet as WalletIcon, Copy, CheckCircle2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { toast } from '../hooks/use-toast';

const DepositModal = ({ open, onClose }) => {
  const { paymentMethods, submitDeposit, refreshPayments } = useApp();
  const [currency, setCurrency] = useState('usdt');
  const [amount, setAmount] = useState('');
  const [txnRef, setTxnRef] = useState('');
  const [selectedPm, setSelectedPm] = useState(null);
  const [copied, setCopied] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) refreshPayments(); }, [open, refreshPayments]);

  useEffect(() => {
    const list = paymentMethods.filter((p) => (currency === 'usdt' ? p.kind === 'USDT' : p.kind === 'UPI'));
    setSelectedPm(list[0] || null);
  }, [currency, paymentMethods]);

  if (!open) return null;

  const methods = paymentMethods.filter((p) => (currency === 'usdt' ? p.kind === 'USDT' : p.kind === 'UPI'));

  const copy = (v, key) => {
    navigator.clipboard.writeText(v);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const submit = async () => {
    if (!amount || Number(amount) <= 0) { toast({ title: 'Enter a valid amount' }); return; }
    if (!txnRef.trim()) { toast({ title: 'Enter transaction reference / hash' }); return; }
    setSubmitting(true);
    try {
      await submitDeposit({ currency, amount, txn_ref: txnRef, payment_method_id: selectedPm?.id });
      toast({ title: 'Deposit submitted', description: 'Admin will verify and credit within 15 min.' });
      setAmount(''); setTxnRef('');
      onClose();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[520px] border border-[#1f1f1f] bg-[#0a0a0a] rounded-md p-6 font-mono my-auto">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 text-[#22c55e]">
            <WalletIcon size={16} />
            <span className="text-[11px] tracking-[0.25em]">DEPOSIT</span>
          </div>
          <button onClick={onClose} className="text-[#5a5a5a] hover:text-[#e6e6e6]"><X size={16} /></button>
        </div>
        <h2 className="text-lg text-[#e6e6e6] mt-1">Add funds to wallet</h2>
        <p className="text-[12px] text-[#7a7a7a] mt-1">Send payment to the address below then submit txn reference for verification.</p>

        <div className="grid grid-cols-2 gap-0 mt-5 border border-[#1f1f1f] rounded-md overflow-hidden">
          <button
            onClick={() => setCurrency('usdt')}
            className={`py-2 text-[11px] tracking-[0.25em] ${currency === 'usdt' ? 'bg-[#22c55e] text-black' : 'text-[#8a8a8a] hover:text-[#e6e6e6]'}`}
          >USDT</button>
          <button
            onClick={() => setCurrency('inr')}
            className={`py-2 text-[11px] tracking-[0.25em] ${currency === 'inr' ? 'bg-[#22c55e] text-black' : 'text-[#8a8a8a] hover:text-[#e6e6e6]'}`}
          >INR (UPI)</button>
        </div>

        {methods.length === 0 ? (
          <div className="mt-5 border border-dashed border-[#1f1f1f] rounded-md p-5 text-center text-[12px] text-[#7a7a7a]">
            No {currency.toUpperCase()} payment methods configured yet. Contact admin.
          </div>
        ) : (
          <div className="mt-5">
            {methods.length > 1 && (
              <div className="flex gap-2 flex-wrap mb-3">
                {methods.map((m) => (
                  <button key={m.id} onClick={() => setSelectedPm(m)}
                    className={`px-3 py-1.5 text-[10px] tracking-[0.2em] rounded-md border ${selectedPm?.id === m.id ? 'border-[#22c55e] text-[#22c55e]' : 'border-[#1f1f1f] text-[#8a8a8a]'}`}>
                    {m.label}{m.network ? ` · ${m.network}` : ''}
                  </button>
                ))}
              </div>
            )}
            {selectedPm && (
              <div className="border border-[#151515] rounded-md p-4 bg-[#0d0d0d]">
                <div className="text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">{selectedPm.label} {selectedPm.network && <span>· {selectedPm.network}</span>}</div>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-[12px] text-[#e6e6e6] break-all">{selectedPm.address}</code>
                  <button onClick={() => copy(selectedPm.address, selectedPm.id)} className="shrink-0 text-[#22c55e] hover:text-[#4ade80]">
                    {copied === selectedPm.id ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                  </button>
                </div>
                {selectedPm.qr_url && (
                  <img src={selectedPm.qr_url} alt="QR" className="w-40 h-40 mt-3 rounded bg-white p-2" />
                )}
                {selectedPm.notes && <p className="text-[11px] text-[#7a7a7a] mt-2">{selectedPm.notes}</p>}
              </div>
            )}
          </div>
        )}

        <div className="mt-5">
          <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1.5">AMOUNT</label>
          <div className="flex items-center border border-[#1f1f1f] rounded-md focus-within:border-[#22c55e] transition-colors">
            <span className="px-3 text-[#5a5a5a] text-sm">{currency === 'usdt' ? '$' : '₹'}</span>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="flex-1 bg-transparent py-2.5 pr-3 text-[#e6e6e6] outline-none text-sm"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1.5">TRANSACTION HASH / REFERENCE</label>
          <input
            value={txnRef}
            onChange={(e) => setTxnRef(e.target.value)}
            placeholder={currency === 'usdt' ? '0x... / TRC20 txid' : 'UPI Ref No.'}
            className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2.5 text-sm text-[#e6e6e6] outline-none focus:border-[#22c55e] transition-colors"
          />
        </div>

        <button
          onClick={submit}
          disabled={submitting}
          className="mt-6 w-full py-2.5 rounded-md text-[11px] tracking-[0.25em] bg-[#166534] hover:bg-[#15803d] disabled:opacity-50 text-[#e7ffef] transition-colors"
        >{submitting ? 'SUBMITTING…' : 'SUBMIT FOR VERIFICATION'}</button>

        <p className="text-[10px] text-[#5a5a5a] mt-3 tracking-wider">Manually verified by admin within 15 minutes. Funds credit after approval.</p>
      </div>
    </div>
  );
};

export default DepositModal;
